import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import SuccessAlert from "../child/Alert/SuccessAlert";
import DengerAlert from "../child/Alert/DengerAlert";
import {
  handleUnauthorized,
  showConfirmationDialog,
} from "../../utils/apiHelper";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useSelector, useDispatch } from "react-redux";
import {
  deleteUserById,
  fetchAllUsers,
  toggleUserStatus,
  deleteUserAlls,
} from "../../redux/user/userThunks";
import { setFilters } from "../../redux/user/userSlice";

const UserListLayer = ({ roles }) => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const authToken = localStorage.getItem("authToken");

  // Inside the component:
  const dispatch = useDispatch();
  const {
    userList: items,
    totalPages,
    totalUsers,
    loading,
    error,
    filters: {
      currentPage,
      itemsPerPage,
      searchQuery,
      sortColumn,
      sortOrder,
      statusFilter,
      roleFilter,
      startDate,
      endDate,
    },
  } = useSelector((state) => state.user);

  // Replace your fetchData with:
  const fetchData = (page) => {
    const formattedStartDate = startDate
      ? moment(startDate).format("YYYY-MM-DD")
      : "";
    const formattedEndDate = endDate
      ? moment(endDate).format("YYYY-MM-DD")
      : "";
    //  Validation: check if start date is greater than end date
    if (
      formattedStartDate &&
      formattedEndDate &&
      moment(formattedStartDate).isAfter(moment(formattedEndDate))
    ) {
      setErrorMessage("Start date cannot be greater than end date");
      setTimeout(() => {
        setErrorMessage("");
      }, 3000);
      return; // stop fetch
    }

    const params = {
      page: page,
      limit: itemsPerPage,
      search: searchQuery,
      sortBy: sortColumn,
      sortOrder: sortOrder,
      status: statusFilter,
      role: roles,
      startDate: formattedStartDate,
      endDate: formattedEndDate,
    };

    dispatch(fetchAllUsers({ params, navigate }));
  };

  useEffect(() => {
  // ✅ Clear only search on full page reload
  dispatch(setFilters({ searchQuery: "" }));
}, [dispatch]);

  useEffect(() => {
    fetchData(currentPage);
  }, [
    dispatch,
    navigate,
    currentPage,
    searchQuery,
    itemsPerPage,
    sortOrder,
    sortColumn,
    statusFilter,
    roleFilter,
    startDate,
    endDate,
  ]);

  const isSelected = (id) => selectedItems.includes(id);
  const handleDeleteAll = async (id, single = "No") => {
    const idsToDelete = single === "Yes" ? [id] : selectedItems;
    if (idsToDelete.length === 0) {
      return;
    }
    const message = `This action will delete the User!`;
    const confirmmessage = `Yes, delete it!`;
    const result = await showConfirmationDialog(message, confirmmessage);

    if (!result.isConfirmed) return;

    try {
      const data = await dispatch(
        deleteUserAlls({ id: idsToDelete, navigate })
      );
      if (data.payload.status === 401) {
        handleUnauthorized(navigate);
        return;
      }

      if (data.payload.status != 200) {
        throw new Error(`HTTP error! status: ${data.payload.status}`);
      }
      const successmessage =
        single === "Yes"
          ? "User deleted successfully"
          : "Users deleted successfully";
      setSuccessMessage(successmessage);
      setTimeout(() => {
        setSuccessMessage("");
        //window.location.reload();
      }, 3000);

      if (single === "No") {
        setSelectedItems([]);
      }
      fetchData(currentPage);
    } catch (error) {
      setErrorMessage(error.message);
      setTimeout(() => {
        setErrorMessage("");
      }, 3000);
      //  }
    }
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      dispatch(setFilters({ currentPage: pageNumber }));
    }
  };

  const handleSort = (column) => {
    const newOrder =
      sortColumn === column && sortOrder === "asc" ? "desc" : "asc";

    dispatch(setFilters({ sortColumn: column, sortOrder: newOrder }));
  };

  const handleItemsPerPageChange = (e) => {
    dispatch(
      setFilters({
        itemsPerPage: parseInt(e.target.value, 10),
        currentPage: 1, // reset to first page
      })
    );
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      dispatch(setFilters({ currentPage: currentPage + 1 }));
    }
  };

  const handlePrev = () => {
    if (currentPage > 1) {
      dispatch(setFilters({ currentPage: currentPage - 1 }));
    }
  };

  const updateStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === "active" ? "inactive" : "active";
    const message = `Do you want to change the status to ${newStatus}?`;

    const result = await showConfirmationDialog(message);
    if (!result.isConfirmed) return;

    try {
      await dispatch(
        toggleUserStatus({ id, data: { status: newStatus }, navigate })
      ).unwrap();
      setSuccessMessage(`User status updated to ${newStatus}`);
    } catch (err) {
      const errorMsg = err?.message || "Failed to update user status";
      setErrorMessage(errorMsg);
    } finally {
      setTimeout(() => {
        setSuccessMessage("");
        setErrorMessage("");
      }, 3000);
    }
  };

  const getPaginationRange = () => {
    let start = Math.max(1, currentPage - 2);
    let end = Math.min(totalPages, start + 4); // Ensure only 5 items are shown (start + 4)

    if (end - start < 4) {
      start = Math.max(1, end - 4);
    }

    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
  };

  const handleDeleteUser = async (id) => {
    const result = await showConfirmationDialog(
      "Are you sure you want to delete this user?"
    );
    if (!result.isConfirmed) return;

    try {
      const data = await dispatch(deleteUserById({ id, navigate })).unwrap();
      setSuccessMessage(data?.message || "User deleted successfully");

      // 🔥 Refresh list after delete
      const newTotalUsers = totalUsers - 1;
      const lastPage = Math.ceil(newTotalUsers / itemsPerPage);

      // if deleting last user on current page, move to previous page
      if (items.length === 1 && currentPage > 1) {
        fetchData(currentPage - 1);
        dispatch(setFilters({ currentPage: currentPage - 1 }));
      } else {
        fetchData(currentPage);
      }
    } catch (err) {
      const errorMsg =
        typeof err === "string" ? err : err?.message || "Failed to delete user";
      setErrorMessage(errorMsg);
    } finally {
      setTimeout(() => {
        setSuccessMessage("");
        setErrorMessage("");
      }, 3000);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedItems(items.map((item) => item._id));
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectItem = (id) => {
    setSelectedItems((prevSelected) => {
      if (prevSelected.includes(id)) {
        return prevSelected.filter((item) => item !== id);
      } else {
        return [...prevSelected, id];
      }
    });
  };

  const calculateAge = (dob) => {
    if (!dob) return null;
    const date = new Date(dob);
    return Math.floor((new Date() - date) / (365.25 * 24 * 60 * 60 * 1000));
  };
  const path = roles === "host" ? "host/view-host" : "user/view-user";
  return (
    <div className="card h-100 p-0 radius-12">
      {successMessage && (
        <SuccessAlert
          message={successMessage}
          onClose={() => setSuccessMessage("")}
        />
      )}
      {errorMessage && (
        <DengerAlert
          message={errorMessage}
          onClose={() => setErrorMessage("")}
        />
      )}
      <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
        <div className="d-flex align-items-center flex-wrap gap-3">
          <span className="text-md fw-medium text-secondary-light mb-0">
            Show
          </span>
          <select
            className="form-select form-select-sm w-auto ps-12 py-6"
            style={{
              fontSize: "16px",
              borderRadius: "7px",
              backgroundPositionX: "unset",
              backgroundPosition: "right 0.75rem center",
            }}
            value={itemsPerPage}
            defaultValue="Select Number"
            onChange={handleItemsPerPageChange}
          >
            <option value="10">10 items per page</option>
            <option value="20">20 items per page</option>
            <option value="50">50 items per page</option>
            <option value="100">100 items per page</option>
          </select>
          <form className="navbar-search" style={{ position: "relative" }}>
            <input
              type="text"
              className="bg-base h-40-px w-auto pe-40"
              name="search"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => {
                dispatch(
                  setFilters({
                    searchQuery: e.target.value,
                  })
                );
              }}
              onKeyUp={(e) => {
                if (e.target.value === "") {
                  dispatch(setFilters({ currentPage: 1 }));
                }
              }}
              style={{
                paddingRight: searchQuery ? "5rem" : "2.5rem",
              }}
            />

            {/* Search Icon */}
            <Icon
              icon="ion:search-outline"
              className="icon"
              style={{
                position: "absolute",
                right: searchQuery ? "3.25rem" : "1rem",
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: "1.25rem",
                color: "#999",
                pointerEvents: "none",
              }}
            />

            {/* Clear (Close) Icon */}
            {searchQuery && (
              <button
                type="button"
                onClick={() => {
                  dispatch(
                    setFilters({
                      searchQuery: "",
                      currentPage: 1,
                    })
                  );
                }}
                style={{
                  position: "absolute",
                  right: "1rem",
                  top: "30%",
                  transform: "translateY(-50%)",
                  fontSize: "1.25rem",
                  color: "#000",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                <Icon icon="ic:round-close" />
              </button>
            )}
          </form>
          <select
            className="form-select form-select-sm w-auto ps-12 py-6"
            style={{
              fontSize: "16px",
              borderRadius: "7px",
              backgroundPositionX: "unset",
              backgroundPosition: "right 0.75rem center",
            }}
            value={statusFilter}
            onChange={(e) =>
              dispatch(
                setFilters({
                  statusFilter: e.target.value,
                  currentPage: 1, // reset to page 1 on filter change
                })
              )
            }
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          {/* <select
            className="form-select form-select-sm w-auto ps-12 py-6"
            style={{
              fontSize: "16px",
              borderRadius: "7px",
              backgroundPositionX: "unset",
              backgroundPosition: "right 0.75rem center",
            }}
            value={roleFilter}
            onChange={(e) =>
              dispatch(
                setFilters({
                  roleFilter: e.target.value,
                  currentPage: 1, // reset to page 1 on filter change
                })
              )
            }
          >
            <option value="">All Roles</option>
            <option value="user">User</option>
            <option value="host">Host</option>
          </select> */}
          <div style={{ position: "relative", maxWidth: "200px" }}>
            <DatePicker
              selected={startDate}
              onChange={(date) => {
                dispatch(
                  setFilters({
                    startDate: date,
                    currentPage: 1,
                  })
                );
              }}
              className="form-control"
              placeholderText="Start Date"
              dateFormat="yyyy-MM-dd"
            />

            {startDate && (
              <button
                type="button"
                onClick={() => {
                  dispatch(
                    setFilters({
                      startDate: null,
                      currentPage: 1,
                    })
                  );
                }}
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "transparent",
                  border: "none",
                  fontSize: "1.25rem",
                  color: "#000",
                  cursor: "pointer",
                  display: "flex",
                }}
              >
                <Icon icon="ic:round-close" />
              </button>
            )}
          </div>

          <div style={{ position: "relative", maxWidth: "200px" }}>
            <DatePicker
              selected={endDate}
              onChange={(date) => {
                dispatch(
                  setFilters({
                    endDate: date,
                    currentPage: 1,
                  })
                );
              }}
              className="form-control"
              placeholderText="End Date"
              dateFormat="yyyy-MM-dd"
            />

            {endDate && (
              <button
                type="button"
                onClick={() => {
                  dispatch(
                    setFilters({
                      endDate: null,
                      currentPage: 1,
                    })
                  );
                }}
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "transparent",
                  border: "none",
                  fontSize: "1.25rem",
                  color: "#000",
                  cursor: "pointer",
                  display: "flex",
                }}
              >
                <Icon icon="ic:round-close" />
              </button>
            )}
          </div>
          <button
            className="btn btn-danger"
            onClick={handleDeleteAll}
            disabled={selectedItems.length === 0}
          >
            Delete All
          </button>
        </div>
      </div>
      <div className="card-body p-24">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table sm-table mb-0">
            <thead>
              <tr>
                <th>
                  <div className="form-check style-check d-flex align-items-center">
                    <input
                      className="form-check-input radius-4 border input-form-dark"
                      type="checkbox"
                      id="selectAll"
                      onChange={handleSelectAll}
                      checked={
                        selectedItems.length === items.length &&
                        items.length > 0
                      }
                    />
                  </div>
                </th>
                {/* <th>
                  <div className="d-flex align-items-center gap-0">Role</div>
                </th> */}
                <th
                  onClick={() => handleSort("fullName")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-0">
                    Name
                    <span className="sort-colume">
                      <Icon
                        icon="mdi:arrow-up"
                        className={`arrow ${
                          sortColumn === "fullName" && sortOrder === "asc"
                            ? "active"
                            : ""
                        }`}
                      />
                      <Icon
                        icon="mdi:arrow-down"
                        className={`arrow ${
                          sortColumn === "fullName" && sortOrder === "desc"
                            ? "active"
                            : ""
                        }`}
                      />
                    </span>
                  </div>
                </th>

                <th
                  onClick={() => handleSort("email")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-0">
                    Email
                    <span className="sort-colume">
                      <Icon
                        icon="mdi:arrow-up"
                        className={`arrow ${
                          sortColumn === "email" && sortOrder === "asc"
                            ? "active"
                            : ""
                        }`}
                      />
                      <Icon
                        icon="mdi:arrow-down"
                        className={`arrow ${
                          sortColumn === "email" && sortOrder === "desc"
                            ? "active"
                            : ""
                        }`}
                      />
                    </span>
                  </div>
                </th>
                <th
                  onClick={() => handleSort("dateOfBirth")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-0">
                    Age
                    <span className="sort-colume">
                      <Icon
                        icon="mdi:arrow-up"
                        className={`arrow ${
                          sortColumn === "dateOfBirth" && sortOrder === "asc"
                            ? "active"
                            : ""
                        }`}
                      />
                      <Icon
                        icon="mdi:arrow-down"
                        className={`arrow ${
                          sortColumn === "dateOfBirth" && sortOrder === "desc"
                            ? "active"
                            : ""
                        }`}
                      />
                    </span>
                  </div>
                </th>
                <th
                  onClick={() => handleSort("createdAt")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-0">
                    Signup date
                    <span className="sort-colume">
                      <Icon
                        icon="mdi:arrow-up"
                        className={`arrow ${
                          sortColumn === "createdAt" && sortOrder === "asc"
                            ? "active"
                            : ""
                        }`}
                      />
                      <Icon
                        icon="mdi:arrow-down"
                        className={`arrow ${
                          sortColumn === "createdAt" && sortOrder === "desc"
                            ? "active"
                            : ""
                        }`}
                      />
                    </span>
                  </div>
                </th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="text-center">
                    <span className="loader-15"></span>
                  </td>
                </tr>
              ) : items.length > 0 ? (
                items.map((user) => (
                  <tr key={user._id}>
                    <td>
                      <div className="form-check style-check d-flex align-items-center">
                        <input
                          className="form-check-input radius-4 border"
                          type="checkbox"
                          value={user._id}
                          checked={isSelected(user._id)}
                          onChange={() => handleSelectItem(user._id)}
                        />
                      </div>
                    </td>
                    {/* <td>
                      <span
                        className="d-inline-block"
                        style={{ maxWidth: "150px" }}
                      >
                        {user?.currentRole || "N/A"}
                      </span>
                    </td> */}
                    <td>
                      <span
                        className="d-inline-block text-truncate"
                        style={{ maxWidth: "150px" }}
                        title={user?.fullName || "N/A"}
                      >
                        {user?.fullName || "N/A"}
                      </span>
                    </td>
                    <td>{user?.email || "N/A"}</td>
                    {/* <td>{user?.age || "N/A"}</td> */}
                    <td>{calculateAge(user.dateOfBirth) || "N/A"}</td>

                    <td>{moment(user?.createdAt).format("DD-MM-YYYY")}</td>
                    <td>
                      <span
                        onClick={() => updateStatus(user?._id, user?.status)}
                        className={`${
                          user.status === "active"
                            ? "bg-success-focus text-success-600 border border-success-main"
                            : user.status === "banned"
                            ? "bg-danger-focus text-danger-600 border border-danger-main"
                            : "bg-neutral-200 text-neutral-600 border border-neutral-400"
                        } px-24 py-4 radius-4 fw-medium text-sm`}
                        style={{ cursor: "pointer" }}
                      >
                        {user.status.charAt(0).toUpperCase() +
                          user.status.slice(1)}
                      </span>
                    </td>
                    <td>
                      <div className="d-flex align-items-center gap-10">
                        <button
                          title="View"
                          onClick={() => navigate(`/${path}/${user._id}`)}
                          className="bg-info-focus text-info-600 w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                        >
                          <Icon icon="majesticons:eye-line" />
                        </button>
                        {/* <button
                          title="Edit"
                          onClick={() =>
                            navigate(`/user/edit-user/${user._id}`)
                          }
                          className="bg-success-focus text-success-600 w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                        >
                          <Icon icon="lucide:edit" />
                        </button> */}
                        <button
                          title="Delete"
                          onClick={() => handleDeleteUser(user._id)}
                          className="bg-danger-focus text-danger-600 w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                        >
                          <Icon icon="fluent:delete-24-regular" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="text-center">
                    No Data Found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
          <span>
            {totalUsers === 0 ? (
              <>No entry to show</>
            ) : (
              <>
                Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                {Math.min(currentPage * itemsPerPage, totalUsers)} of{" "}
                {totalUsers} entries
              </>
            )}
          </span>

          <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center">
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <Link
                className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                to="#"
                onClick={handlePrev}
              >
                <Icon icon="ep:d-arrow-left" />
              </Link>
            </li>
            {getPaginationRange().map((page) => {
              // Return JSX for pagination items
              return (
                <li key={page} className="page-item">
                  <Link
                    className={`page-link text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md ${
                      currentPage === page
                        ? "bg-primary-600 text-white"
                        : "bg-neutral-200"
                    }`}
                    to="#"
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </Link>
                </li>
              );
            })}

            <li
              className={`page-item ${
                currentPage === totalPages ? "disabled" : ""
              }`}
            >
              <Link
                className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                to="#"
                onClick={handleNext}
              >
                <Icon icon="ep:d-arrow-right" />
              </Link>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default UserListLayer;
